import type { BuilderProduct } from "api-types/builders/builderTypes";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { StudioDocUserRole } from "api-types/studioDocs/studioDocTypes";
import type { UserAdminRole } from "api-types/users/userAdminRole";
import type { ShowroomFloorPlanPayload } from "building-types/building/showroomFloorPlanPayloadTypes";
import type { PolicyJson } from "common/cedar/cedarTypes";
import type { Modify } from "common/utils/types";
import type { ClientBuilderUserHydrated } from "../builderUsers/builderUsersResponses";
import type { ClientCreditHydrated } from "../credits/creditsResponses";
import type { ClientOption } from "../options/optionsResponses";
import type { ClientBuilderRole } from "../permissions/permissionsResponses";
import type { ClientStudioDocMetadata } from "../studioDocs/studioDocMetadataTypes";
import type { ClientBuilder } from "./clientBuilderTypes";

export const clientBuilderProductDisplayNames: { [key in BuilderProduct]: string } = {
  api: "API",
  procure: "Procure",
  showroom: "Showroom",
  studio: "Studio",
  autoRenders: "AutoRender",
} as const;

export type ClientBuilderWithCredits = {
  builder: ClientBuilder;
  credits: ClientCreditHydrated[];
};

export type ClientBuilderAdminDetails = {
  builder: ClientBuilder;
  masterPlans: ClientMasterPlan[];
  builderUsers: ClientBuilderUserHydrated[];
  credits: ClientCreditHydrated[];
  metrics: ClientBuilderPlanMetrics[];
};

export type ClientBuilderPlanMetrics = {
  builderId: string;
  planId: string;
  planType: "studio_docs" | "master_plans" | "community_plans";
  planName: string;
  homePlansCreated: number;
  homePlansBuilt: number;
};

export type ClientStudioDocMetadataForBuilder = {
  studioDoc: ClientStudioDocMetadata;
  users: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    adminRole: UserAdminRole;
    userRole?: StudioDocUserRole;
  }[];
};

interface EncodedOptionFields {
  refId?: string;
  id: string;
  builderId: string;
  optionSetId: string;
}
export type ClientBuilderOption = Omit<Modify<ClientOption, EncodedOptionFields>, "docOptionId">;

export type ClientBuilderPoliciesAndRoles = {
  policies: PolicyJson[];
  roles: ClientBuilderRole[];
};

export type ClientBuilderPlanGltfTimingSpan = {
  startTimeMs?: number;
  durationMs: number;
};

export type ClientBuilderPlanGltfTiming = {
  buildingGeneration: ClientBuilderPlanGltfTimingSpan;
  geometryCollection: ClientBuilderPlanGltfTimingSpan;
  materialParamsResolution?: ClientBuilderPlanGltfTimingSpan;
  gltfSerialization: ClientBuilderPlanGltfTimingSpan;
  sourcePipelineTotal: ClientBuilderPlanGltfTimingSpan;
  sourceResponseTotal?: ClientBuilderPlanGltfTimingSpan;
};

export type ClientBuilderPlanGltfResponse = {
  gltfBase64: string;
  planData: ClientHomePlanBaseData;
  timing: ClientBuilderPlanGltfTiming;
  /** Precomputed Showroom floor plan required by server-side Showroom rendering. */
  floorPlan: ShowroomFloorPlanPayload;
};

export type ClientBuilderPlanGltfOptionChangeResponse = {
  gltfBase64: string;
  timing: ClientBuilderPlanGltfTiming;
  /** Refreshed floor plan for the new option choices. */
  floorPlan: ShowroomFloorPlanPayload;
};
